import React, { createContext, useContext, useState, useEffect } from 'react';
import { Work, sampleWorks } from '../models/Work';
// Since we're now using JSON directly, we don't need the CSV parser
// import { parseCSV } from '../utils/csvParser';

interface WorksContextType {
  works: Work[];
  addWork: (work: Omit<Work, 'id'>) => void;
  updateWork: (id: string, work: Partial<Work>) => void;
  deleteWork: (id: string) => void;
  loading: boolean;
}

const WorksContext = createContext<WorksContextType | undefined>(undefined);

export const useWorks = () => {
  const context = useContext(WorksContext);
  if (!context) {
    throw new Error('useWorks must be used within a WorksProvider');
  }
  return context;
};

export const WorksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadWorks = async () => {
      try {
        // For development: Clear localStorage to force loading from JSON file
        // Comment this out in production if you want to keep user edits
        localStorage.removeItem('works');
        
        // Check if there are works in localStorage
        const savedWorks = localStorage.getItem('works');
        
        if (savedWorks) {
          setWorks(JSON.parse(savedWorks));
          setLoading(false);
          return;
        }
        
        // If no saved works, load from the JSON file
        // The JSON file should be in the public/data folder
        const response = await fetch('/data/works.json');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Parse the JSON data
        const jsonWorks = await response.json();
        
        if (jsonWorks.length > 0) {
          setWorks(jsonWorks);
          localStorage.setItem('works', JSON.stringify(jsonWorks));
        } else {
          // Fallback to sample works if JSON loading failed
          setWorks(sampleWorks);
          localStorage.setItem('works', JSON.stringify(sampleWorks));
        }
      } catch (error) {
        console.error('Error loading works:', error);
        // Fallback to sample works if JSON loading failed
        setWorks(sampleWorks);
        localStorage.setItem('works', JSON.stringify(sampleWorks));
      } finally {
        setLoading(false);
      }
    };
    
    loadWorks();
  }, []);

  const saveWorks = (newWorks: Work[]) => {
    localStorage.setItem('works', JSON.stringify(newWorks));
    setWorks(newWorks);
  };

  const addWork = (work: Omit<Work, 'id'>) => {
    const newWork = {
      ...work,
      id: Date.now().toString(), // Simple ID generation for demo
    };
    const newWorks = [...works, newWork];
    saveWorks(newWorks);
  };

  const updateWork = (id: string, work: Partial<Work>) => {
    const newWorks = works.map(w => 
      w.id === id ? { ...w, ...work } : w
    );
    saveWorks(newWorks);
  };

  const deleteWork = (id: string) => {
    const newWorks = works.filter(w => w.id !== id);
    saveWorks(newWorks);
  };

  return (
    <WorksContext.Provider value={{ works, addWork, updateWork, deleteWork, loading }}>
      {children}
    </WorksContext.Provider>
  );
};

export default WorksContext; 